
import api from '@/api'
import Tabs from '@/components/my-work/tabs.vue'
import UnavailableDays from '@/components/my-work/unavailable-days.vue'
import { formatDate, parseDate } from '@/utils'
import { MyWorkTab } from '@/interfaces'
import { Calendar } from '@fullcalendar/core'
import { defineComponent, onMounted, reactive, ref } from 'vue'
import { addHours, eachDayOfInterval, isSameDay } from 'date-fns'
import calendarConfig from '@/components/my-work/calendar-config'
import { isBusinessDay } from '@/assets/isBusinessDay'
import { notAvailableArgs } from '@/utils'
import { UnavailableDaysDto } from 'service/__generated-api'

import {
  CreateDayDto,
  DayDto,
  DayTemplateDto,
  DeleteDayDto
} from 'service/__generated-api'

export default defineComponent({
  components: { Tabs, UnavailableDays },
  setup() {
    const activeTab = ref(MyWorkTab.Template)
    let calendar: Calendar | null = null

    // TabLoader activates in applyResolver and terminates in calendar.events method
    // /shrug calendar refetchEvents doesn't return a promise
    const isLoading = ref(false)
    const unavailableDays = ref<UnavailableDaysDto>()

    async function updateUnavailableDays() {
      try {
        const { data } = await api.days.getUnavailableDays()
        unavailableDays.value = data
      } catch (error) {
        unavailableDays.value = undefined
      }
    }

    async function applyResolver<T>(promise: Promise<T>): Promise<T> {
      if (isLoading.value === true) Promise.reject()
      isLoading.value = true
      const res = await promise
      calendar?.refetchEvents()
      return res
    }

    async function applyToSelection(dayTemplate: DayTemplateDto) {
      const days: CreateDayDto[] = selectedDays.value.map(formattedDate => {
        return { formattedDate, ...dayTemplate }
      })

      await applyResolver(api.days.upsertMany(days))
      updateUnavailableDays()
    }

    async function applyNotAvailableToSelection({
      comment,
      halfNotAvailable,
      atConference,
      unpaid,
      occasionalAbsence,
      notAvailable,
      halfNotAvailableStartTime,
      halfNotAvailableEndTime,
      markedByPartTimeUser
    }: notAvailableArgs) {
      const days: CreateDayDto[] = selectedDays.value.map(formattedDate => ({
        comment,
        halfNotAvailable,
        atConference,
        unpaid,
        occasionalAbsence,
        halfNotAvailableStartTime,
        halfNotAvailableEndTime,
        formattedDate,
        periods: '',
        remote: false,
        holiday: false,
        notAvailable,
        markedByPartTimeUser
      }))

      await applyResolver(api.days.upsertMany(days))
      updateUnavailableDays()
    }

    async function removeSelection() {
      const daysToRemove: DeleteDayDto[] = selectedDays.value.map(
        formattedDate => ({ formattedDate })
      )

      await applyResolver(api.days.deleteMany(daysToRemove))
      activeTab.value = MyWorkTab.Template
      updateUnavailableDays()
    }

    const calendarEl = ref()
    const selectedDays = ref<string[]>([])
    const selectedDay = reactive<DayDto>({} as DayDto)

    onMounted(() => {
      calendar = new Calendar(calendarEl.value as HTMLDivElement, {
        ...calendarConfig,
        async events(info) {
          const formattedStartDate = formatDate(info.start)
          const formattedEndDate = formatDate(info.end)

          const daysRes = await api.days.getDays(
            formattedStartDate,
            formattedEndDate
          )

          isLoading.value = false
          selectedDays.value = []

          return daysRes.data.map(day => {
            return {
              id: day.monthYearUser,
              allDay: true,
              start: parseDate(day.formattedDate),
              extendedProps: { day }
            }
          })
        },
        select({ start, end }) {
          end = addHours(end, -1)
          const isTheSameDay = isSameDay(start, end)

          selectedDays.value = eachDayOfInterval({ start, end })
            .filter(date => isTheSameDay || isBusinessDay(formatDate(date)))
            .map(formatDate)

          if (selectedDays.value.length === 1) {
            const selectedEvent = calendar
              ?.getEvents()
              .find(
                event =>
                  event._def.extendedProps.day.formattedDate ===
                  formatDate(start)
              )?._def

            activeTab.value = selectedEvent?.extendedProps.day.periods
              ? MyWorkTab.Custom
              : MyWorkTab.Template

            if (selectedEvent) {
              Object.assign(selectedDay, selectedEvent.extendedProps.day)
            }
          }

          if (selectedDays.value.length > 1) {
            activeTab.value = MyWorkTab.Template
            selectedDay.periods = '08:00-16:00'
            selectedDay.remote = false
          }
        }
      })

      calendar.render()

      updateUnavailableDays()
    })

    return {
      applyToSelection,
      applyNotAvailableToSelection,
      removeSelection,
      calendarEl,
      selectedDays,
      selectedDay,
      activeTab,
      MyWorkTab,
      isLoading,
      unavailableDays
    }
  }
})
